import { getErrorMessage } from '@monetize/utils/core';
import { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {
  MButton,
  MFlex,
  MHeading,
  MPageContainer,
  MText,
} from '../../../components/Monetize';
import { ROUTES } from '../../../constants';
import { useAuth } from '../../../services/auth0';
import Sentry from '../../../services/sentry';
import { useToast } from '../../../services/toast';

/**
 * Shown to a signed-in user who has not been invited to any tenant. Tenants
 * are created for customers by MonetizeNow, so there is nothing to do here but
 * wait for an invitation.
 */
export const NoTenantAccess = () => {
  const {
    user,
    accessToken,
    getTenantIdsFromAccessToken,
    getAndHandleAccessTokenSilently,
  } = useAuth();
  const { addToast } = useToast();
  const navigate = useNavigate();
  const [isChecking, setIsChecking] = useState(false);

  // The token loads asynchronously, so a reload can mount this page before the
  // token that grants a tenant has arrived
  useEffect(() => {
    if (getTenantIdsFromAccessToken(accessToken).length > 0) {
      navigate(ROUTES.ROOT);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [accessToken]);

  // An invite sent after login only shows up in a freshly issued token
  const checkForInvite = async () => {
    setIsChecking(true);
    try {
      const { updatedAccessToken } = await getAndHandleAccessTokenSilently({
        forceRefresh: true,
      });
      if (getTenantIdsFromAccessToken(updatedAccessToken).length > 0) {
        Sentry.logger.info('NoTenantAccess - invite found, continuing');
        navigate(ROUTES.ROOT);
        return;
      }
      addToast({
        severity: 'info',
        summary: 'No invitation yet',
        detail: 'Once your administrator invites you, try again.',
      });
    } catch (ex) {
      Sentry.logger.error('NoTenantAccess - token refresh failed', {
        error: getErrorMessage(ex),
      });
      addToast({
        severity: 'error',
        summary: 'Error',
        detail: 'We could not check for an invitation. Please try again.',
      });
    } finally {
      setIsChecking(false);
    }
  };

  return (
    <MPageContainer alignItems="center" justifyContent="center">
      <MFlex direction="column" align="center" maxW="md" textAlign="center">
        <MHeading fontSize="xl" mb="2">
          You don't have access yet
        </MHeading>
        <MText color="fg.muted" mb="2">
          {user?.email ? <b>{user.email}</b> : 'Your account'} hasn't been
          invited to a MonetizeNow account.
        </MText>
        <MText color="fg.muted">
          Ask your MonetizeNow administrator to send you an invitation, then
          check again.
        </MText>
        <MFlex mt="6" gap="3">
          <MButton
            variant="secondary"
            asChild
            data-testid="no-tenant-access-logout-btn"
          >
            <Link to={ROUTES.AUTH_LOGOUT}>Log out</Link>
          </MButton>
          <MButton
            variant="primary"
            onClick={checkForInvite}
            isLoading={isChecking}
            data-testid="no-tenant-access-check-again-btn"
          >
            Check again
          </MButton>
        </MFlex>
      </MFlex>
    </MPageContainer>
  );
};
